import { LabelItem } from "@landtechnologies/components";
import { useEffect, useMemo, useState } from "react";
import { useAddLabelMutation } from "../SaveSiteButton/useAddLabel/useAddLabel";
import { useRemoveLabelMutation } from "../SaveSiteButton/useRemoveLabel";
import { useCreateLabel } from "../SaveSiteButton/useCreateLabel";
import { useUpdateLabel } from "../SaveSiteButton/useUpdateLabel";
import { useArchiveLabel } from "../SaveSiteButton/useArchiveLabel/useArchiveLabel";

interface UseLabelsProps {
  siteLabels: LabelItem[];
  labelOptions: LabelItem[];
  siteId: string;
  startedSaving(): void;
  stoppedSaving(): void;
}

export const useLabels = ({
  siteLabels,
  labelOptions,
  siteId,
  startedSaving,
  stoppedSaving,
}: UseLabelsProps) => {
  const [allLabels, setAllLabels] = useState<LabelItem[]>(labelOptions);
  const [labels, setLabels] = useState<LabelItem[]>(siteLabels);
  const { addLabelMutation } = useAddLabelMutation();
  const { removeLabelMutation } = useRemoveLabelMutation();
  const { createLabelMutation } = useCreateLabel();
  const { updateLabelMutation } = useUpdateLabel();
  const { archiveLabelMutation } = useArchiveLabel();

  useEffect(() => {
    setAllLabels(labelOptions);
  }, [labelOptions]);

  const addLabel = (labelId: string) => {
    if (labels.find((label) => label.id === labelId)) return;

    const labelToAdd = allLabels.find((label) => label.id === labelId);
    labelToAdd && setLabels([...labels, labelToAdd]);
  };

  const removeLabel = (labelId: string) => {
    const newLabels = labels.filter((label) => label.id !== labelId);
    setLabels(newLabels);
  };

  const labelsDiff = useMemo(
    () => ({
      labelsToAdd: labels.filter(
        (label) => !siteLabels.find((siteLabel) => siteLabel.id === label.id)
      ),
      labelsToRemove: siteLabels.filter(
        (siteLabel) => !labels.find((label) => label.id === siteLabel.id)
      ),
    }),
    [labels, siteLabels]
  );

  useEffect(() => {
    const { labelsToAdd, labelsToRemove } = labelsDiff;

    async function syncLabels() {
      const labelsToRemovePromises = labelsToRemove.map((label) =>
        removeLabelMutation({ siteId, labelId: label.id })
      );

      const labelsToAddPromises = labelsToAdd.map((label) =>
        addLabelMutation({ siteId, labelId: label.id })
      );

      startedSaving();
      Promise.all([...labelsToAddPromises, ...labelsToRemovePromises]).finally(stoppedSaving);
    }

    if (labelsToAdd.length || labelsToRemove.length) {
      syncLabels();
    }
  }, [labelsDiff, addLabelMutation, removeLabelMutation, siteId, startedSaving, stoppedSaving]);

  return {
    allLabels,
    labels,
    siteLabels,
    addLabel,
    setLabels,
    labelsDiff,
    removeLabel,
    createLabelMutation,
    updateLabelMutation,
    archiveLabelMutation,
  };
};
